// reCAPTCHA v3 utility for Summary Maps
// Loads and executes Google reCAPTCHA v3 for login protection

let scriptPromise = null;
let scriptLoaded = false;

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

/**
 * Load the reCAPTCHA script dynamically
 */
export function loadRecaptcha() {
  if (scriptLoaded || !SITE_KEY) {
    return Promise.resolve();
  }
  
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.grecaptcha) {
      scriptLoaded = true;
      window.grecaptcha.ready(resolve);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      scriptLoaded = true;
      if (window.grecaptcha) {
        window.grecaptcha.ready(resolve);
      } else {
        resolve();
      }
    };
    
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load reCAPTCHA'));
    };
    
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * Execute reCAPTCHA and get a token for the specified action
 * @param {string} action - The action name (e.g., 'login', 'register')
 * @returns {Promise<string>} - The reCAPTCHA token
 */
export async function getRecaptchaToken(action = 'login') {
  if (!SITE_KEY) {
    console.warn('reCAPTCHA site key not configured');
    return null;
  }

  try {
    await loadRecaptcha();
    
    if (!window.grecaptcha) {
      throw new Error('reCAPTCHA not available');
    }

    const token = await window.grecaptcha.execute(SITE_KEY, { action });
    return token;
  } catch (error) {
    console.error('reCAPTCHA error:', error);
    return null;
  }
}

/**
 * Check if reCAPTCHA is configured
 */
export function isRecaptchaConfigured() {
  return Boolean(SITE_KEY);
}

export default {
  loadRecaptcha,
  getRecaptchaToken,
  isRecaptchaConfigured
};
